import { METRICS, type MetricType } from 'features/charts-v2/types'

/**
 * Augment user-selected bubble metrics with both count metrics for weighted averaging.
 *
 * V1 bubble charts compute volume-weighted averages:
 * - Avg Rating weighted by review count (COUNT(POST))
 * - Avg Sentiment weighted by sentence count (COUNT(POST_TEXT))
 *
 * To replicate this, we need both count metrics in the API response regardless of
 * which one the user chose for bubble size. This function appends the missing count
 * metrics to the user's selection.
 *
 * @param selectedMetrics - User's 3 selected metrics [x-axis, y-axis, bubble-size]
 * @returns Augmented metrics array with both record_count and sentence_count appended if missing
 */
export function getAugmentedBubbleMetrics(
  selectedMetrics: MetricType[],
): MetricType[] {
  const augmented = [...selectedMetrics]

  // Ensure both count metrics are present
  if (!augmented.includes(METRICS.RECORD_COUNT.id)) {
    augmented.push(METRICS.RECORD_COUNT.id)
  }
  if (!augmented.includes(METRICS.SENTENCE_COUNT.id)) {
    augmented.push(METRICS.SENTENCE_COUNT.id)
  }

  return augmented
}

/**
 * Determine which count metric to use as the weight for a given axis metric.
 *
 * V1 rule (from backend/applications/api/src/api/routers/charts/v1/chart_types/bubble/query.py):
 * - Sentiment is weighted by sentence count (COUNT(POST_TEXT))
 * - Everything else (rating, NPS, etc.) is weighted by review count (COUNT(POST))
 *
 * @param axisMetric - The metric displayed on the axis
 * @returns The count metric ID to use as the weight
 */
export function getWeightMetricForAxis(
  axisMetric: MetricType,
): 'record_count' | 'sentence_count' {
  return axisMetric === METRICS.SENTIMENT.id ? 'sentence_count' : 'record_count'
}

/**
 * Compute volume-weighted average: sum(value * weight) / sum(weight).
 *
 * Matches V1 backend logic in fill_origin_data (backend/.../v1/utils.py line 478).
 *
 * @param values - Array of metric values to average
 * @param weights - Array of volume weights (same length as values)
 * @returns Weighted average, rounded to 2 decimal places. Falls back to simple average if total weight is 0.
 */
export function computeWeightedAverage(
  values: number[],
  weights: number[],
): number {
  if (values.length === 0) return 0
  if (values.length !== weights.length) {
    throw new Error(
      'computeWeightedAverage: values and weights must have same length',
    )
  }

  const totalWeight = weights.reduce((sum, w) => sum + w, 0)

  // Fallback to simple average if total weight is 0
  if (totalWeight === 0) {
    return values.reduce((sum, v) => sum + v, 0) / values.length
  }

  const weightedSum = values.reduce(
    (sum, value, idx) => sum + value * (weights[idx] ?? 0),
    0,
  )

  return Math.round((weightedSum / totalWeight) * 100) / 100
}
